import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRoles } from "@/hooks/useUserRoles";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, Users, Calendar, MessageSquare, ArrowRight, AlertCircle, CheckCircle2, Megaphone, CalendarDays, ExternalLink, ClipboardList } from "lucide-react";
import { format, parseISO, isFuture } from "date-fns";
import { useDashboardEvents } from "@/hooks/useEvents";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUnacknowledgedAssignments, useAcknowledgeAssignments } from "@/hooks/useAssignmentsAdmin";
import { NewAssignmentsDialog } from "@/components/lms/NewAssignmentsDialog";
import { DashboardLeaderboard } from "@/components/dashboard/DashboardLeaderboard";
import { AssignmentsWidget } from "@/components/dashboard/AssignmentsWidget";
import { ConnectionLeaderboard } from "@/components/dashboard/ConnectionLeaderboard";
import { useNeedsAttentionCount } from "@/hooks/useStudentAssignments";
import { useLowConnectionsTrigger } from "@/hooks/useSuggestedConnections";
import { SuggestedConnectionsModal } from "@/components/SuggestedConnectionsModal";
import { PendingSurveysCard } from "@/components/dashboard/PendingSurveysCard";
import { LearningProgressWidget } from "@/components/dashboard/LearningProgressWidget";
import { TopShopStatusCard } from "@/components/topshop/TopShopStatusCard";

type AudienceRole = "admin" | "mentor" | "instructor" | "entrepreneur";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: userRoles } = useUserRoles();
  const { data: unacknowledgedAssignments } = useUnacknowledgedAssignments();
  const acknowledgeAssignments = useAcknowledgeAssignments();
  const { data: dashboardEvents } = useDashboardEvents();
  const { data: needsAttentionCount } = useNeedsAttentionCount();
  const [showAssignmentsDialog, setShowAssignmentsDialog] = useState(false);
  const [hasShownDialog, setHasShownDialog] = useState(false);

  // Fetch connection stats early so we can use it for the low connections trigger
  const { data: connectionStats } = useQuery({
    queryKey: ["connection_stats", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: pending } = await supabase
        .from("connections")
        .select("id", { count: "exact" })
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .eq("status", "pending");

      const { data: active } = await supabase
        .from("connections")
        .select("id", { count: "exact" })
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .eq("status", "accepted");

      const { data: completed } = await supabase
        .from("connections")
        .select("id", { count: "exact" })
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .not("mentorship_completed_at", "is", null);

      return {
        pending: pending?.length || 0,
        active: active?.length || 0,
        completed: completed?.length || 0,
      };
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Low connections trigger for suggested connections modal
  const { shouldShow: showLowConnectionsModal, dismiss: dismissLowConnectionsModal } =
    useLowConnectionsTrigger({
      userId: user?.id,
      activeConnectionCount: connectionStats?.active,
      hasCompletedSuggestedConnections: profile?.has_completed_suggested_connections,
    });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  // Show new assignments dialog when there are unacknowledged assignments
  useEffect(() => {
    if (
      !hasShownDialog &&
      unacknowledgedAssignments &&
      (unacknowledgedAssignments.curricula.length > 0 ||
        unacknowledgedAssignments.courses.length > 0)
    ) {
      setShowAssignmentsDialog(true);
      setHasShownDialog(true);
    }
  }, [unacknowledgedAssignments, hasShownDialog]);

  const handleAcknowledgeAssignments = async () => {
    if (!unacknowledgedAssignments) return;

    await acknowledgeAssignments.mutateAsync({
      curriculumIds: unacknowledgedAssignments.curricula.map((c) => c.id),
      courseIds: unacknowledgedAssignments.courses.map((c) => c.id),
    });
    setShowAssignmentsDialog(false);
  };

  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ["is_admin", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
      return data as boolean;
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // Admin status rarely changes - cache for 10 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch announcements from community_announcements with audience filtering
  const { data: announcements } = useQuery({
    queryKey: ["dashboard_announcements", user?.id, profile?.role, userRoles, isAdmin],
    queryFn: async () => {
      // Fetch sent announcements, pinned first, then by created_at
      const { data: announcementsData, error: announcementsError } = await supabase
        .from("community_announcements")
        .select("*")
        .eq("status", "sent")
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });
      
      if (announcementsError) throw announcementsError;
      if (!announcementsData || announcementsData.length === 0) return [];

      // Fetch audiences for all announcements
      const announcementIds = announcementsData.map(a => a.id);
      const { data: audiencesData, error: audiencesError } = await supabase
        .from("community_announcement_audiences")
        .select("*")
        .in("announcement_id", announcementIds);

      if (audiencesError) throw audiencesError;

      // Combine announcements with their audiences
      const withAudiences = announcementsData.map(a => ({
        ...a,
        audiences: (audiencesData || [])
          .filter(aud => aud.announcement_id === a.id)
          .map(aud => aud.audience_role as AudienceRole),
      }));

      // Admins see all announcements
      if (isAdmin) return withAudiences.slice(0, 3);

      // Filter based on user's role
      const profileRole = profile?.role;
      const hasUserRole = (role: string) => userRoles?.includes(role as any) || false;

      return withAudiences.filter(announcement => {
        // If no audiences specified, show to everyone
        if (announcement.audiences.length === 0) return true;
        
        // Check profile role (entrepreneur, mentor)
        if (announcement.audiences.includes("entrepreneur") && profileRole === "entrepreneur") return true;
        if (announcement.audiences.includes("mentor") && profileRole === "mentor") return true;

        // Check user_roles table (admin, instructor, mentor)
        if (announcement.audiences.includes("admin") && hasUserRole("admin")) return true;
        if (announcement.audiences.includes("instructor") && hasUserRole("instructor")) return true;
        if (announcement.audiences.includes("mentor") && hasUserRole("mentor")) return true;
        
        return false;
      }).slice(0, 3);
    },
    enabled: !!user && !!profile && isAdmin !== undefined,
    staleTime: 5 * 60 * 1000, // Cache announcements for 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: weeklyRequests } = useQuery({
    queryKey: ["weekly_requests", user?.id],
    queryFn: async () => {
      if (!user?.id || profile?.role !== "entrepreneur") return null;

      const { data, error } = await supabase.rpc("get_weekly_pending_requests", {
        _user_id: user.id,
      });

      if (error) throw error;
      return data as number;
    },
    enabled: !!user?.id && profile?.role === "entrepreneur",
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Check if connection limit is enabled
  const { data: connectionLimitEnabled } = useQuery({
    queryKey: ["site_setting_connection_limit"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "connection_limit_enabled")
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data?.value === "true";
    },
    staleTime: 10 * 60 * 1000, // Site settings rarely change - cache for 10 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Check if leaderboard is enabled
  const { data: leaderboardEnabled } = useQuery({
    queryKey: ["site_setting_leaderboard_dashboard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "leaderboard_enabled")
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data?.value === "true";
    },
    staleTime: 10 * 60 * 1000, // Site settings rarely change - cache for 10 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Check if connection leaderboard is enabled
  const { data: connectionLeaderboardEnabled } = useQuery({
    queryKey: ["site_setting_connection_leaderboard_dashboard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "connection_leaderboard_enabled")
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data?.value === "true";
    },
    staleTime: 10 * 60 * 1000, // Site settings rarely change - cache for 10 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Calculate profile completion
  const getProfileCompletion = () => {
    if (!profile) return 0;
    let filled = 0;
    let total = 3; // Base fields: full_name, bio, avatar_url
    
    if (profile.full_name) filled++;
    if (profile.bio) filled++;
    if (profile.avatar_url) filled++;
    
    return Math.round((filled / total) * 100);
  };

  const profileCompletion = getProfileCompletion();
  const isEntrepreneur = profile?.role === "entrepreneur";
  const remainingRequests = 3 - (weeklyRequests || 0);

  // Filter to only upcoming events (future dates)
  const upcomingEvents = dashboardEvents?.filter((event) => isFuture(parseISO(event.event_date))).slice(0, 3) || [];

  return (
    <DashboardLayout>
      {(authLoading || profileLoading) ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (!user || !profile) ? null : (
        <>
        <div className="space-y-6 animate-fade-in">
        <TopShopStatusCard />
        {/* Pending Surveys */}
        <PendingSurveysCard />
        {/* Announcements Section */}
        {announcements && announcements.length > 0 && (
          <Card className="border-info/50 bg-info/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Megaphone className="h-5 w-5 text-info" />
                Announcements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {announcements.map((announcement) => (
                <div key={announcement.id} className="border-l-2 border-info pl-3">
                  <p className="font-medium text-sm">{announcement.title}</p>
                  <div
                    className="text-sm text-muted-foreground line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: announcement.body }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(announcement.created_at).toLocaleDateString()}
                  </p>
                  {announcement.cta_label && announcement.cta_url && (
                    <div className="mt-2">
                      <Button variant="outline" size="sm" asChild>
                        <a href={announcement.cta_url} target="_blank" rel="noopener noreferrer">
                          {announcement.cta_label}
                          <ExternalLink className="h-3 w-3 ml-2" />
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              <Button variant="link" className="p-0 h-auto text-info" onClick={() => navigate("/announcements")}>
                View all announcements →
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Events Section */}
        {upcomingEvents.length > 0 && (
          <Card className="border-accent/50 bg-accent/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="h-5 w-5 text-accent" />
                Upcoming Events
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {upcomingEvents.map((event) => {
                const featuredImage = event.attachments?.find((a) => a.is_featured && a.attachment_type === "image");
                return (
                  <div key={event.id} className="flex gap-3 border-l-2 border-accent pl-3">
                    {featuredImage && (
                      <img
                        src={featuredImage.url}
                        alt={event.title}
                        className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{event.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(parseISO(event.event_date), "MMM d, yyyy 'at' h:mm a")}
                        {event.timezone && ` (${event.timezone})`}
                      </p>
                      {event.registration_link && (
                        <a
                          href={event.registration_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-accent hover:underline inline-flex items-center gap-1 mt-1"
                        >
                          Register <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
              <Button variant="link" className="p-0 h-auto text-accent" onClick={() => navigate("/events")}>
                View all events →
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Assignments Need Attention Card */}
        {needsAttentionCount !== undefined && needsAttentionCount > 0 && (
          <Link to="/curricula">
            <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 hover:shadow-md transition-shadow cursor-pointer dark:from-orange-950/30 dark:to-amber-950/30 dark:border-orange-800">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-100 dark:bg-orange-900/50 rounded-xl">
                      <ClipboardList className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Assignments Need Attention</h3>
                      <p className="text-muted-foreground text-sm">
                        {needsAttentionCount} assignment{needsAttentionCount !== 1 ? 's need' : ' needs'} your attention
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                      {needsAttentionCount}
                    </span>
                    <Button variant="outline" className="gap-2">
                      View Assignments
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Welcome back, {profile.full_name?.split(" ")[0]}!</h1>
            <p className="text-muted-foreground">
              {isEntrepreneur
                ? "Find mentors to help grow your business"
                : "Connect with entrepreneurs seeking guidance"}
            </p>
          </div>
          <Button onClick={() => navigate("/directory")} className="bg-accent hover:bg-accent/90">
            Browse Directory
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Alerts */}
        {!profile.is_published && (
          <Card className="border-warning/50 bg-warning/5">
            <CardContent className="flex items-center gap-4 py-4">
              <AlertCircle className="h-5 w-5 text-warning flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Profile Pending Approval</p>
                <p className="text-sm text-muted-foreground">
                  An administrator will review and publish your profile soon. Complete your profile to speed up approval.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/profile")}>
                Complete Profile
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Requests</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{connectionStats?.pending || 0}</div>
              <p className="text-xs text-muted-foreground">Awaiting response</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Connections</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{connectionStats?.active || 0}</div>
              <p className="text-xs text-muted-foreground">Ongoing mentorships</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed Calls</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{connectionStats?.completed || 0}</div>
              <p className="text-xs text-muted-foreground">Sessions finished</p>
            </CardContent>
          </Card>

          {isEntrepreneur && connectionLimitEnabled !== false && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Weekly Requests</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{remainingRequests}/3</div>
                <p className="text-xs text-muted-foreground">Mentor requests remaining</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Profile Completion */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Completion</CardTitle>
            <CardDescription>Complete your profile to get better matches</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Progress value={profileCompletion} className="flex-1" />
              <span className="text-sm font-medium">{profileCompletion}%</span>
            </div>
            {profileCompletion < 100 && (
              <Button variant="outline" onClick={() => navigate("/profile")}>
                Complete Your Profile
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Learning Assignments Widget */}
        <LearningProgressWidget />

        <AssignmentsWidget />

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="card-hover cursor-pointer" onClick={() => navigate("/directory")}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Browse Directory
              </CardTitle>
              <CardDescription>
                Find mentors and entrepreneurs in the EAB Connect community
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="card-hover cursor-pointer" onClick={() => navigate("/connections")}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-accent" />
                My Connections
              </CardTitle>
              <CardDescription>View and manage your mentorship connections</CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Leaderboards Section */}
        {(leaderboardEnabled || connectionLeaderboardEnabled) && (
          <div className="grid gap-4 md:grid-cols-2">
            {leaderboardEnabled && <DashboardLeaderboard />}
            {connectionLeaderboardEnabled && <ConnectionLeaderboard />}
          </div>
        )}
      </div>

      {/* New Assignments Dialog */}
      <NewAssignmentsDialog
        open={showAssignmentsDialog}
        onOpenChange={setShowAssignmentsDialog}
        curricula={unacknowledgedAssignments?.curricula || []}
        courses={unacknowledgedAssignments?.courses || []}
        onStartLearning={handleAcknowledgeAssignments}
        onLater={handleAcknowledgeAssignments}
      />

      {/* Suggested Connections Modal for users with low connections */}
      <SuggestedConnectionsModal
        isOpen={showLowConnectionsModal}
        onClose={dismissLowConnectionsModal}
        currentUserId={user?.id || ""}
        currentUserRole={profile?.role || "entrepreneur"}
      />
        </>
      )}
    </DashboardLayout>
  );
}
